/**
 * Textos das telas de conta, compra e extensão (proposta "Elements", 17/10/2026): ativação,
 * transferência, espera, conflito, confirmação de email, compra (CTA), conta bloqueada/inativa,
 * manutenção, celular, ativação por link, barra de licença, extensão, 404, pesquisa de remoção e
 * lotes descartados. O português fica como padrão no código (chaves `as_*` em src/i18n/locales).
 *
 * `{{theProduct}}`/`{{ofProduct}}` levam o artigo certo em português ("a Máquina de Leads",
 * "do eMiner"); nos outros idiomas valem o nome puro.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { formatNumber, numericDateLocale } from '../../lib/format';
import { tokenMs } from '../../lib/movimento';
import { localeOf } from '../../features/home/homeText';

export const SCREEN_PT = {
  terms: 'Termos de Uso',
  privacy: 'Política de Privacidade',
  close: 'Fechar',
  cancel: 'Cancelar',
  continue: 'Continuar',
  retry: 'Tentar de novo',
  back: 'Voltar',

  act_title: 'Libere seu acesso',
  terms_resp: 'Você é totalmente responsável pelo uso dos dados coletados com {{theProduct}}.',
  terms_accept: 'Li e aceito os',
  terms_docs: 'Termos de Uso e a Política de Privacidade',
  act_email: 'Seu email',
  act_code: 'Código de ativação',
  act_receive: 'Receber o código por email',
  act_sending: 'Enviando…',
  act_sent: 'Enviamos o código para {{email}}. Confira também a caixa de spam.',
  act_sent_if: 'Se houver um código para {{email}}, ele chega em instantes. Confira também a caixa de spam.',
  act_limited: 'Você já pediu o código algumas vezes. Tente de novo em {{n}} min.',
  act_not_found: 'Não encontramos uma compra com {{email}}.',
  act_expired: 'Este código expirou. Fale com o suporte para receber um novo.',
  act_submit: 'Liberar o acesso',
  act_submitting: 'Liberando…',
  err_email: 'Escreva um email válido, como nome@empresa.com.',
  err_code_short: 'O código tem pelo menos 5 caracteres.',
  err_invalid: 'Email ou código de ativação inválido.',
  err_code_used: 'Este código já foi usado.',
  err_inactive: 'Esta conta está inativa. Fale com o suporte para reativar.',
  err_generic: 'Algo deu errado. Tente de novo em instantes.',
  trial_title: 'Seu período de testes já foi usado',
  trial_text: 'Se gostou do que viu, contrate um plano para continuar usando {{theProduct}}.',
  see_plans_short: 'Ver os planos',
  em_title: 'Entre no {{product}}',
  em_text: 'Escreva o seu email. Enviamos um link para confirmar que ele é seu.',
  em_email: 'Seu email',

  tr_other: 'Outro computador',
  tr_this: 'Este computador',
  tr_title: 'Esta licença está em uso em outro computador',
  tr_one: 'Só um computador usa a licença por vez.',
  tr_block: 'Ao transferir, o outro computador fica bloqueado por <b>{{hours}} horas</b>.',
  tr_rule: 'Essa regra está nos',
  tr_agree: 'Concordo em transferir o acesso para este computador',
  tr_activate: 'Ativar neste computador',

  wait_title: 'O acesso está em outro computador',
  wait_text: 'Como só um computador usa a licença por vez, este pode pegar o acesso de volta em:',
  wait_hm: '{{h}} h {{m}} min',
  wait_m: '{{m}} min',
  wait_less: 'menos de 1 min',
  wait_until: 'Libera em {{date}}, às {{time}}',
  wait_done: 'Pronto: o tempo de espera acabou.',
  reactivate: 'Reativar',
  reactivate_now: 'Já pode reativar',
  conf_title: 'Seu acesso foi para outro computador',
  conf_text:
    'A licença passou a ser usada em outro computador e, como ela funciona em um por vez, o acesso aqui foi interrompido. Para voltar a usar este computador, é só reativar.',
  confirm_title: 'Confirme seu email',
  confirm_text:
    'Enviamos um link para <b>{{email}}</b>. Abra o email e clique no link: esta tela segue sozinha quando você confirmar.',
  confirm_waiting: 'Esperando a confirmação',
  confirm_spam: 'Não chegou? Veja a caixa de spam.',
  confirm_change: 'Alterar o email',
  confirmed: 'Email confirmado',
  going: 'Levando você ao painel…',

  link_activating: 'Ativando sua conta…',
  link_done: 'Conta ativada',
  link_error_title: 'Não conseguimos ativar com este link',
  link_error_text:
    'Confira se o link está completo, igual ao do email. Se preferir, ative com o email da compra e o código de ativação.',
  link_missing_title: 'Este link de ativação não é válido',
  link_missing_text:
    'Falta o código de ativação no link. Use o link completo do email, ou ative com o email da compra e o código.',
  link_manual: 'Ativar com email e código',

  blocked_title: 'Conta bloqueada',
  blocked_text:
    'Observamos que o uso desta conta fere os nossos termos de uso e, por isso, ela foi bloqueada automaticamente. Recomendamos que você interrompa tentativas de criar contas fantasmas, usar emails falsos, tentar violar a segurança do sistema ou violar nossa propriedade intelectual.',
  blocked_terms: 'Ler os Termos de Uso',
  inactive_title: 'Sua conta {{ofProduct}} está inativa',
  inactive_text: 'Para voltar a usar, fale com o suporte. Vamos ajudar a reativar a sua conta.',
  support: 'Falar com o suporte',
  maint_title: 'Estamos em manutenção',
  maint_text: 'O sistema volta em alguns minutos. Obrigado pela paciência.',
  mobile_title: '{{TheProduct}} funciona no computador',
  mobile_text:
    'A captura roda numa extensão do Google Chrome, no computador. Abra este link por lá:',
  copy: 'Copiar o link',
  copied: 'Link copiado',

  cta_trial_title: 'Desbloqueie {{theProduct}}',
  cta_trial_text:
    'Você está a um passo de acessar leads ilimitados e funcionalidades avançadas. Libere todo o potencial da ferramenta e tenha uma máquina previsível de novos clientes todos os dias.',
  cta_expired_title: 'Seu acesso {{toProduct}} expirou',
  cta_expired_text: 'Renove seu acesso para continuar utilizando todas as vantagens {{ofProduct}}.',
  cta_em_title: 'Disponível nos planos {{ofProduct}}',
  cta_em_text:
    'Contrate um plano para desbloquear todas as funcionalidades {{ofProduct}} agora mesmo.',
  cta_le_title: 'Desbloqueie o LeadExplorer',
  cta_le_text:
    'Contrate o <b>LeadExplorer</b> por <b>1 mês</b> e tenha acesso à ferramenta capaz de gerar dossiês repletos de dados estratégicos para suas vendas.',
  stat_contacts_trial: 'contatos capturados no seu teste',
  stat_contacts: 'contatos capturados',
  stat_lists: 'listas criadas',
  stat_business: 'negócios nas suas listas',
  stat_lists_saved: 'listas guardadas',
  b_unlimited: 'Captura ilimitada, em todas as fontes',
  b_download: 'Baixar todas as suas listas',
  b_tools: 'Semelhantes e Enriquecer dados',
  b_all: 'Todas as funcionalidades {{ofProduct}}',
  see_plans: 'Ver planos disponíveis',
  already: 'Já comprei? Liberar o acesso',
  contract: 'Contratar',
  unlock_title: 'Liberar o acesso agora',
  released: 'Acesso liberado',
  active_until: 'Licença ativa até {{date}}',

  lic_active: 'Licença ativa até <b>{{date}}</b> · {{days}}',
  lic_em: 'Seus superpoderes terminam em <b>{{days}}</b> · {{date}}',
  lic_today: 'Sua licença termina hoje, em <b>{{time}}</b>',
  lic_ended:
    'Licença encerrada em <b>{{date}}</b>. Renove para continuar capturando leads sem limites.',
  lic_lifetime: 'Licença vitalícia',
  lic_sub: 'Assinatura com renovação automática',
  days_one: '1 dia',
  days_other: '{{n}} dias',
  renew: 'Renovar',

  ext_install: 'Instalar extensão',
  ext_title: 'Instale a extensão para continuar',
  ext_text:
    'Este recurso usa a extensão {{ofProduct}}, que faz as buscas e a captura no seu Chrome.',
  ext_s1: 'Instale a extensão na Chrome Web Store',
  ext_s2: 'Volte para esta aba',
  ext_s3: 'Clique em <b>Já instalei</b>',
  ext_installed: 'Já instalei',
  ext_verifying: 'Verificando…',
  ext_store: 'Instalar na Chrome Web Store',
  ext_missing_title: 'Ainda não encontramos a extensão',
  ext_missing_text:
    'Se você acabou de instalar, recarregue a página para o painel enxergar a extensão.',
  reload_page: 'Recarregar a página',
  rec_title: 'A extensão foi atualizada',
  rec_text: 'Recarregue o painel para reconectar. O que você preencheu continua aqui.',
  rec_reload: 'Recarregar o painel',
  rec_capture_title: 'Há uma captura em andamento',
  rec_capture_text:
    'Para não perder os leads que estão chegando, pare a captura antes de recarregar.',
  rec_open: 'Abrir a captura',
  drop_title: 'Alguns leads da última captura podem não ter sido salvos.',
  drop_one:
    '1 lote da captura foi descartado porque a fila da extensão encheu. Para recuperar, rode a mesma busca de novo.',
  drop_other:
    '{{n}} lotes da captura foram descartados porque a fila da extensão encheu. Para recuperar, rode a mesma busca de novo.',

  nf_code: 'Erro 404',
  nf_title: 'Página não encontrada',
  nf_text:
    'Não encontramos a página que você tentou acessar. Ela pode ter sido removida ou o endereço está incorreto.',
  nf_home: 'Voltar ao Início',
  nf_or: 'Ou vá direto para:',
  nf_lists: 'Ver minhas listas',
  nf_capture: 'Capturar leads',

  un_title: 'Que pena ver você sair',
  un_text: 'Pode contar por que removeu a extensão? Marque um ou mais motivos.',
  un_reasons: 'Motivos',
  r_no_need: 'Não estou precisando agora',
  r_dont_understand: 'Não entendi como funciona',
  r_not_working: 'Não está funcionando direito',
  r_not_accurate: 'Os dados não eram precisos',
  r_alternative: 'Encontrei uma alternativa melhor',
  r_expensive: 'É muito caro',
  r_other_reason: 'Outros motivos',
  send: 'Enviar',
  sending: 'Enviando…',
  un_hint: 'Marque pelo menos um motivo para enviar.',
  un_error: 'Não conseguimos enviar agora. Verifique a conexão e tente de novo.',
  un_thanks: 'Obrigado por contar',
  un_thanks_text: 'Sua resposta ajuda a melhorar {{theProduct}}. Esperamos nos encontrar de novo.',
  go_site: 'Ir para o site',
  reinstall: 'Removeu sem querer? Instale de novo',
} as const;

export type ScreenKey = keyof typeof SCREEN_PT;

type Vars = Record<string, string | number>;

export function useScreenText() {
  const { t, i18n } = useTranslation();
  const brand = useBrand();
  const lang = (i18n?.language ?? 'pt').slice(0, 2);
  const locale = localeOf(lang);
  const product = brand.appTitle;

  const words = useMemo<Vars>(() => {
    if (lang !== 'pt') {
      return {
        product,
        theProduct: product,
        TheProduct: product,
        ofProduct: product,
        toProduct: product,
      };
    }
    const female = brand.source === 'lead-machine';
    return {
      product,
      theProduct: `${female ? 'a' : 'o'} ${product}`,
      TheProduct: `${female ? 'A' : 'O'} ${product}`,
      ofProduct: `${female ? 'da' : 'do'} ${product}`,
      toProduct: `${female ? 'à' : 'ao'} ${product}`,
    };
  }, [lang, product, brand.source]);

  const tx = useCallback(
    (key: ScreenKey, vars?: Vars): string => {
      const all = { ...words, ...vars };
      const out: unknown = t(`as_${key}`, {
        defaultValue: SCREEN_PT[key],
        ...all,
        interpolation: { escapeValue: false },
      });
      if (typeof out === 'string') return out;
      return SCREEN_PT[key].replace(/\{\{(\w+)\}\}/g, (_m, name: string) =>
        String(all[name] ?? ''),
      );
    },
    [t, words],
  );
  const plural = useCallback(
    (base: 'days' | 'drop', n: number): string =>
      tx(`${base}_${(lang === 'fr' ? n <= 1 : n === 1) ? 'one' : 'other'}` as ScreenKey, {
        n: formatNumber(n, locale),
      }),
    [lang, locale, tx],
  );
  const number = useCallback((n: number): string => formatNumber(n, locale), [locale]);
  const date = useCallback(
    (value: Date | string): string =>
      new Intl.DateTimeFormat(numericDateLocale(locale), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(value)),
    [locale],
  );
  const time = useCallback(
    (value: Date | string): string =>
      new Intl.DateTimeFormat(numericDateLocale(locale), {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value)),
    [locale],
  );

  return { tx, plural, number, date, time, lang, product };
}

/** Escapa o que vem do usuário antes de entrar num texto com HTML (<b>, <a>). */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Email no formato nome@empresa.com. */
export function isEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/* ---------- Movimento das telas (23/10/2026) ----------
   Hooks pequenos de comportamento usados por várias telas; ficam aqui porque Screen.tsx só
   exporta componentes (fast refresh). */

/**
 * Para a janela que o pai desmonta ao fechar (ex.: Fale conosco, aberto pelo cabeçalho): `fechar`
 * toca a saída da ScreenDialog (passe `aberta` em `open`) e só então chama `onClose`. Sem
 * duração (testes), chama na hora. `fecharDepois(ms)` fecha sozinha passado esse prazo (ex.: a
 * confirmação que fica à vista); ele divide o timer com a saída, então "Fechar" antes do prazo ou
 * desmontar a janela o cancela, e um prazo velho nunca fecha a janela que abriu depois.
 */
export function useFecharComSaida(
  onClose: () => void,
  saida: string = '--duracao-140',
): { aberta: boolean; fechar: () => void; fecharDepois: (ms: number) => void } {
  const [aberta, setAberta] = useState(true);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const fechar = useCallback(() => {
    window.clearTimeout(timer.current);
    const ms = tokenMs(saida);
    if (ms <= 0) {
      onClose();
      return;
    }
    setAberta(false);
    timer.current = window.setTimeout(onClose, ms);
  }, [onClose, saida]);
  const fecharDepois = useCallback(
    (ms: number) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(fechar, ms);
    },
    [fechar],
  );
  return { aberta, fechar, fecharDepois };
}

/**
 * Halo da ação amarela (M12 b): uma vez por tela, quando ela passa a ficar disponível. A ação que
 * já nasce disponível não ganha halo; se apagar e acender de novo, acende sem halo (a classe
 * fica e a animação não recomeça). `tela` muda quando a mesma ação volta numa tela nova (ex.: um
 * novo aviso de transferência). Com "reduzir movimento", o CSS não mostra o halo.
 */
export function useHaloUmaVez(disponivel: boolean, tela?: unknown): boolean {
  const [estado, setEstado] = useState({ tela, antes: disponivel, halo: false });
  if (estado.tela !== tela) {
    setEstado({ tela, antes: disponivel, halo: false });
  } else if (disponivel !== estado.antes) {
    setEstado({ tela, antes: disponivel, halo: estado.halo || disponivel });
  }
  return estado.halo;
}
